import { Heading, TextLink, TextPassage } from '@petsmart-ui/sparky';
import type { ComponentType, MouseEvent, SVGProps } from 'react';

import { getBaseUrlJoined, trackNavigationHeaderClick } from '@libs/analytics';
import type { CurrentTierLevel } from '@libs/common-types';
import { handleAccountClick } from '@libs/experience-api';

import TreatsRewardsIcon from '../../../shared/treats-rewards.icon.svg';
import { useAccountAuthenticated } from '../../use-account-authenticated';
import { BestieIcon, MemberIcon, VippIcon } from './icons';

import './details.scss';

const TREATS_REWARDS_HREF = '/treats-rewards/authenticated';

type TierIcon = ComponentType<SVGProps<SVGSVGElement>>;

const TIERS: Record<CurrentTierLevel, { tierLabel: string; TierIcon: TierIcon }> = {
    member: { tierLabel: 'Member', TierIcon: MemberIcon },
    bestie: { tierLabel: 'Bestie', TierIcon: BestieIcon },
    vipp: { tierLabel: 'VIPP', TierIcon: VippIcon },
};

type DetailsProps = {
    onAction: () => void;
};

type TreatsRewardsClickInput = {
    event: MouseEvent<Element>;
    selection: string;
};

export function Details({ onAction }: DetailsProps) {
    const { firstName, loyaltyData } = useAccountAuthenticated();
    const tierData = loyaltyData ? TIERS[loyaltyData.currentTierLevel] : null;

    async function handleTreatsRewardsClick({ event, selection }: TreatsRewardsClickInput) {
        trackNavigationHeaderClick(selection, getBaseUrlJoined(TREATS_REWARDS_HREF) ?? TREATS_REWARDS_HREF);
        onAction();
        await handleAccountClick({ event: event as MouseEvent<HTMLAnchorElement>, href: TREATS_REWARDS_HREF });
    }

    return (
        <div className="account-authenticated-details">
            <Heading className="account-authenticated-details__greeting" tagName="h2" size="body-lg-bold">
                Hi, <span className="account-authenticated-details__capitalize">{firstName}</span>!
            </Heading>

            {loyaltyData && tierData && (
                <>
                    <p className="account-authenticated-details__tier">
                        {tierData.tierLabel}
                        <tierData.TierIcon aria-hidden="true" className="account-authenticated-details__tier-icon" />
                    </p>

                    <div className="account-authenticated-details__balance">
                        <Heading tagName="h3" size="body-bold">
                            ${loyaltyData.availableDollars} Treats balance
                        </Heading>
                        <TextPassage className="account-authenticated-details__points">
                            ({loyaltyData.availablePoints} points)
                        </TextPassage>
                    </div>
                </>
            )}

            <div className="account-authenticated-details__treats">
                <a
                    href={TREATS_REWARDS_HREF}
                    aria-label="Treats Rewards"
                    onClick={async event => {
                        await handleTreatsRewardsClick({ event, selection: 'treatsrewards' });
                    }}
                >
                    <TreatsRewardsIcon
                        aria-label="Treats Rewards"
                        className="account-authenticated-details__treats-logo"
                    />
                </a>
                <TextLink
                    className="account-authenticated-details__see-benefits"
                    href={TREATS_REWARDS_HREF}
                    onClick={async event => {
                        await handleTreatsRewardsClick({ event, selection: 'see benefits' });
                    }}
                >
                    See benefits
                </TextLink>
            </div>
        </div>
    );
}
